import React, { useState } from 'react';
import { Calculator, TrendingUp, ArrowRight, AlertCircle, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../config/api';

const MiniSimulateurRetraiteAnticipee = () => {
  const [ageActuel, setAgeActuel] = useState(55);
  const [ageRetraitePrevu, setAgeRetraitePrevu] = useState(60);
  const [capitalActuel, setCapitalActuel] = useState(500000);
  const [depensesMensuelles, setDepensesMensuelles] = useState(6000);
  const [result, setResult] = useState(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const calculateRetraite = () => {
    const ageRetraiteNormal = 65;
    const anneesAnticipation = ageRetraiteNormal - ageRetraitePrevu;
    const anneesJusquaRetraite = ageRetraitePrevu - ageActuel;
    
    // Calcul de la rente AVS réduite (6.8% de réduction par année d'anticipation, max 2 ans)
    const anneesAnticipationAVS = Math.min(anneesAnticipation, 2); // AVS max 2 ans d'anticipation
    const reductionAVS = anneesAnticipationAVS * 6.8;
    const renteAVSNormale = 2520; // CHF/mois max 2025
    const renteAVSAnticipee = renteAVSNormale * (1 - (reductionAVS / 100));
    
    // Calcul de la rente LPP (estimation)
    const capitalLPPEstime = capitalActuel * 0.6; // 60% du capital en LPP
    const tauxConversionLPP = 0.065; // 6.5%
    const renteLPPAnnuelle = capitalLPPEstime * tauxConversionLPP;
    const renteLPPMensuelle = renteLPPAnnuelle / 12;
    
    // Calcul du manque à combler
    const revenusRetraite = renteAVSAnticipee + renteLPPMensuelle;
    const manqueACombler = depensesMensuelles - revenusRetraite;
    
    // Capital nécessaire pour combler le manque
    const anneesVie = 90 - ageRetraitePrevu; // Espérance jusqu'à 90 ans
    const capitalNecessaire = manqueACombler > 0 ? manqueACombler * 12 * anneesVie : 0;
    
    // Déterminer la faisabilité
    let faisabilite = '';
    let couleur = '';
    if (capitalNecessaire === 0 || capitalActuel >= capitalNecessaire * 1.2) {
      faisabilite = 'Excellent ! Retraite anticipée viable';
      couleur = 'text-green-400';
    } else if (capitalActuel >= capitalNecessaire * 0.8) {
      faisabilite = 'Possible avec optimisations';
      couleur = 'text-yellow-400';
    } else {
      faisabilite = 'Difficile sans ajustements majeurs';
      couleur = 'text-red-400';
    }
    
    setResult({
      anneesAnticipation,
      reductionAVS: reductionAVS.toFixed(1),
      renteAVSAnticipee,
      renteLPPMensuelle,
      revenusRetraite,
      manqueACombler: Math.max(0, manqueACombler),
      capitalNecessaire,
      faisabilite,
      couleur
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleSendPDF = async () => {
    if (!email || !result) return;
    
    setLoading(true);
    
    // Sauvegarder la position actuelle du scroll
    const scrollPosition = window.scrollY;
    
    try {
      const response = await fetch(`${API_BASE_URL}/lead-magnet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          guide: 'Guide Planification Retraite Anticipée',
          position: 'simulator',
          simulator_data: {
            ageActuel: ageActuel,
            ageRetraitePrevu: ageRetraitePrevu,
            capitalActuel: capitalActuel,
            depensesMensuelles: depensesMensuelles,
            anneesAnticipation: result.anneesAnticipation,
            reductionAVS: result.reductionAVS,
            renteAVSAnticipee: result.renteAVSAnticipee,
            renteLPPMensuelle: result.renteLPPMensuelle,
            revenusRetraite: result.revenusRetraite,
            manqueACombler: result.manqueACombler,
            capitalNecessaire: result.capitalNecessaire,
            faisabilite: result.faisabilite
          }
        })
      });
      
      if (response.ok) {
        setSuccess(true);
        
        // Restaurer la position du scroll
        setTimeout(() => {
          window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
        }, 0);
        
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Error sending PDF:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10" id="simulateur">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-gradient-to-br from-purple-500 to-pink-600 p-3 rounded-xl">
          <Calculator className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
          🎯 Calculez votre faisabilité de retraite anticipée
        </h3>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Votre âge actuel
          </label>
          <input
            type="range"
            min="45"
            max="64"
            step="1"
            value={ageActuel}
            onChange={(e) => setAgeActuel(Number(e.target.value))}
            className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-right text-2xl font-bold text-white mt-2">
            {ageActuel} ans
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Âge de retraite souhaité
          </label>
          <input
            type="range"
            min={ageActuel + 1}
            max="65"
            step="1"
            value={ageRetraitePrevu}
            onChange={(e) => setAgeRetraitePrevu(Number(e.target.value))}
            className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-right text-2xl font-bold text-white mt-2">
            {ageRetraitePrevu} ans
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Capital actuel (2ème + 3ème pilier) (CHF)
          </label>
          <input
            type="range"
            min="100000"
            max="3000000"
            step="50000"
            value={capitalActuel}
            onChange={(e) => setCapitalActuel(Number(e.target.value))}
            className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-right text-2xl font-bold text-white mt-2">
            {formatCurrency(capitalActuel)}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Dépenses mensuelles souhaitées à la retraite (CHF)
          </label>
          <input
            type="range"
            min="3000"
            max="15000"
            step="500"
            value={depensesMensuelles}
            onChange={(e) => setDepensesMensuelles(Number(e.target.value))}
            className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-right text-2xl font-bold text-white mt-2">
            {formatCurrency(depensesMensuelles)}
          </div>
        </div>

        <button
          onClick={calculateRetraite}
          className="w-full bg-gradient-to-r from-purple-500 to-pink-600 text-white px-6 py-4 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-700 transition-all transform hover:scale-105 flex items-center justify-center gap-2"
        >
          <Calculator className="w-5 h-5" />
          Calculer ma faisabilité
        </button>

        {result && (
          <div className="mt-6 p-6 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-500/30">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-6 h-6 text-purple-400" />
              <h4 className="text-xl font-bold text-white">Votre projection</h4>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Anticipation :</span>
                <span className="text-xl font-bold text-white">{result.anneesAnticipation} ans</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Réduction rente AVS :</span>
                <span className="text-lg font-bold text-red-400">-{result.reductionAVS}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Rente AVS anticipée/mois :</span>
                <span className="text-lg font-bold text-white">{formatCurrency(result.renteAVSAnticipee)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Rente LPP estimée/mois :</span>
                <span className="text-lg font-bold text-white">{formatCurrency(result.renteLPPMensuelle)}</span>
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-white/10">
                <span className="text-gray-300">Revenus totaux/mois :</span>
                <span className="text-2xl font-bold text-purple-400">{formatCurrency(result.revenusRetraite)}</span>
              </div>
              {result.manqueACombler > 0 && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Manque à combler/mois :</span>
                    <span className="text-xl font-bold text-yellow-400">{formatCurrency(result.manqueACombler)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Capital nécessaire :</span>
                    <span className="text-2xl font-bold text-white">{formatCurrency(result.capitalNecessaire)}</span>
                  </div>
                </>
              )}
              <div className="mt-4 p-4 bg-blue-500/20 rounded-lg border border-blue-500/30">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-blue-300 mb-1">Faisabilité :</p>
                    <p className={`font-bold text-lg ${result.couleur}`}>{result.faisabilite}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
              <p className="text-sm text-gray-300 mb-3">
                📧 Recevez votre rapport personnalisé par email
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder="Votre email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                />
                <button
                  onClick={handleSendPDF}
                  disabled={!email || loading}
                  className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {loading ? 'Envoi...' : 'Recevoir'}
                </button>
              </div>
              {success && (
                <p className="text-green-400 text-sm mt-2">✅ Rapport envoyé avec succès ! Consultez votre boîte mail.</p>
              )}
            </div>
            
            <Link
              to="/contact"
              className="mt-4 w-full bg-white text-purple-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all flex items-center justify-center gap-2"
            >
              Planifier ma retraite anticipée avec ATOM
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniSimulateurRetraiteAnticipee;
